import { AppButton } from "components/button/AppButton";
import { Dropdown, Input, Menu, Popconfirm, Skeleton, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useQueryClient } from "react-query";
import { openNotification } from "utils/notification";
import { hasPermission } from "utils/hasPermission";
import {
  useGetContextTips,
  QUERY_KEY_CONTEXT_TIPS,
} from "../hooks/useGetContextTips";
import { useDeleteContextTip } from "../hooks/useDeleteContextTip";
import { TContextTip } from "../types";
import { AddContextTip } from "./AddContextTip";
import { EditContextTip } from "./EditContextTip";

export const ContextTipsTable = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState<TContextTip | null>(null);

  const { token, userInfo } = useGetUserInfo();
  const canManage = hasPermission(userInfo, "Manage Context Tips");
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useGetContextTips(page, search);
  const { mutate: remove, isLoading: deleting } = useDeleteContextTip();

  const columns: ColumnsType<TContextTip> = [
    {
      title: "Icon",
      key: "icon",
      width: 80,
      render: (_, record) =>
        record.icon_url ? (
          <img
            src={record.icon_url}
            alt={record.title}
            className="h-10 w-10 object-contain rounded bg-gray-50 border border-gray-200"
          />
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title) => <span className="font-medium">{title}</span>,
    },
    {
      title: "Subtitle",
      dataIndex: "subtitle",
      key: "subtitle",
      render: (subtitle) => (
        <span className="text-gray-600 text-sm">{subtitle || "—"}</span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 48,
      render: (_, record) =>
        canManage ? (
          <Dropdown
            trigger={["click"]}
            overlay={
              <Menu>
                <Menu.Item
                  key="edit"
                  onClick={() => {
                    setSelected(record);
                    setEditOpen(true);
                  }}
                >
                  <i className="ri-edit-2-fill"></i> Edit
                </Menu.Item>
                <Menu.Item key="delete">
                  <Popconfirm
                    title="Delete this context tip?"
                    onConfirm={() =>
                      remove(
                        { token, id: record.id },
                        {
                          onSuccess: () => {
                            openNotification({
                              title: "Deleted",
                              state: "success",
                              description: "Context tip deleted",
                              duration: 3,
                            });
                            queryClient.invalidateQueries({
                              queryKey: [QUERY_KEY_CONTEXT_TIPS],
                            });
                          },
                          onError: () => {
                            openNotification({
                              title: "Error",
                              state: "error",
                              description: "Could not delete context tip",
                              duration: 4,
                            });
                          },
                        },
                      )
                    }
                    okButtonProps={{ loading: deleting }}
                  >
                    <span className="text-red-600">
                      <i className="ri-delete-bin-line"></i> Delete
                    </span>
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            }
          >
            <i className="ri-more-2-fill text-lg cursor-pointer"></i>
          </Dropdown>
        ) : null,
    },
  ];

  return (
    <div>
      {addOpen && (
        <AddContextTip open={addOpen} handleClose={() => setAddOpen(false)} />
      )}
      {editOpen && selected && (
        <EditContextTip
          open={editOpen}
          tip={selected}
          handleClose={() => {
            setEditOpen(false);
            setSelected(null);
          }}
        />
      )}

      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-end mb-4">
        <div>
          <h4 className="font-semibold text-lg text-primary">Context tips</h4>
          <p className="text-sm text-gray-600">
            Manage tip cards with title, subtitle, and icon for in-app guidance.
          </p>
        </div>
        {canManage && (
          <AppButton
            label="New context tip"
            handleClick={() => setAddOpen(true)}
          />
        )}
      </div>

      <div className="mb-4">
        <Input.Search
          placeholder="Search title or subtitle"
          allowClear
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={(v) => {
            setSearch(v.trim());
            setPage(1);
          }}
          className="max-w-xs"
        />
      </div>

      {isLoading ? (
        <Skeleton active />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.data ?? []}
          loading={isFetching}
          pagination={{
            current: data?.current_page ?? page,
            total: data?.total ?? 0,
            pageSize: data?.per_page ?? 15,
            onChange: setPage,
          }}
        />
      )}
    </div>
  );
};
